// ---------------------------------------------------------------------------
// Cookie Consent Utility
// Pure client-side module — no "server-only", no cookies() from next/headers.
// Reads/writes document.cookie directly. Safe to import in "use client" components.
// ---------------------------------------------------------------------------

export type CookieCategory = "essential" | "analytics" | "marketing"

export type ConsentCategories = Record<CookieCategory, boolean>

export interface ConsentState {
  categories: ConsentCategories
  timestamp: string
}

export interface CategoryDefinition {
  id: CookieCategory
  required: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CONSENT_COOKIE_NAME = "cookie-consent"

export const COOKIE_CATEGORIES: CategoryDefinition[] = [
  { id: "essential", required: true },
  { id: "analytics", required: false },
  { id: "marketing", required: false },
]

// ---------------------------------------------------------------------------
// getConsent(): ConsentState | null
// Reads and parses the cookie-consent cookie from document.cookie.
// Returns null if missing or malformed — safe default triggers banner.
// ---------------------------------------------------------------------------

export function getConsent(): ConsentState | null {
  if (typeof document === "undefined") return null

  const cookies = document.cookie.split(";")
  for (const cookie of cookies) {
    const trimmed = cookie.trim()
    const eqIndex = trimmed.indexOf("=")
    if (eqIndex === -1) continue
    const name = trimmed.substring(0, eqIndex).trim()
    if (name !== CONSENT_COOKIE_NAME) continue
    const value = trimmed.substring(eqIndex + 1).trim()
    if (!value) return null
    try {
      const decoded = decodeURIComponent(value)
      const parsed: unknown = JSON.parse(decoded)
      // Validate shape before trusting the parsed cookie data
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        !("categories" in parsed) ||
        !("timestamp" in parsed) ||
        typeof (parsed as ConsentState).timestamp !== "string" ||
        typeof (parsed as ConsentState).categories !== "object" ||
        (parsed as ConsentState).categories === null
      ) {
        return null
      }
      return parsed as ConsentState
    } catch {
      // FM1: Malformed cookie — return null to trigger banner re-show
      return null
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// setConsent(categories): void
// Writes the cookie-consent cookie with categories + timestamp.
// Always forces essential: true regardless of input.
// Cookie attributes: path=/, max-age=31536000 (365 days), SameSite=Lax, Secure (HTTPS)
// ---------------------------------------------------------------------------

export function setConsent(categories: ConsentCategories): void {
  const state: ConsentState = {
    categories: {
      ...categories,
      essential: true, // Always forced true — essential cookies are exempt
    },
    timestamp: new Date().toISOString(),
  }
  const encoded = encodeURIComponent(JSON.stringify(state))
  // Add Secure flag when served over HTTPS to prevent cookie interception over HTTP
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${CONSENT_COOKIE_NAME}=${encoded}; path=/; max-age=31536000; SameSite=Lax${secure}`
}

// ---------------------------------------------------------------------------
// hasConsent(category): boolean
// Returns true if the given category is consented to.
// Returns false if no consent exists or category is false.
// ---------------------------------------------------------------------------

export function hasConsent(category: CookieCategory): boolean {
  const state = getConsent()
  if (!state) return false
  return state.categories[category] === true
}
